"use client"
import { parseAsArrayOf, parseAsStringLiteral, useQueryState } from "nuqs"
import { useFilterDefaults } from "@/components/layout/FilterDefaultsProvider"
import { QUERY_PARAM_KEYS } from "./keys"

const STATUSES = ["listed", "ownedByYou", "all", "hidden", "notListed"] as const

export type Status = (typeof STATUSES)[number]

export function useStatusQueryParam() {
  const { statuses: defaultStatuses } = useFilterDefaults()

  const [statuses, setStatuses] = useQueryState(
    QUERY_PARAM_KEYS.status,
    parseAsArrayOf(parseAsStringLiteral<Status>(STATUSES)).withDefault(
      defaultStatuses ?? [],
    ),
  )

  async function onSelectStatus(status: Status) {
    if (status === "all" && !statuses.includes("all")) {
      await setStatuses(["all"])
      return
    }

    const newStatuses = (
      statuses.includes(status)
        ? statuses.filter(s => s !== status)
        : [...statuses, status]
    )
      // Filter out "all" if there are other statuses
      .filter(s => s !== "all")
      // Ensure "listed" and "hidden" are mutually exclusive
      .filter(s => {
        if (status === "listed") {
          return s !== "hidden" && s !== "notListed"
        }
        if (status === "notListed") {
          return s !== "listed" && s !== "hidden"
        }
        if (status === "hidden") {
          return s !== "listed" && s !== "notListed"
        }
        return true
      })
    await setStatuses(newStatuses.length > 0 ? newStatuses : ["all"])
  }

  async function removeStatus(status: Status) {
    const newStatuses = statuses.filter(s => s !== status)
    await setStatuses(newStatuses.length > 0 ? newStatuses : ["all"])
  }

  async function addStatus(status: Status) {
    if (statuses.includes(status)) {
      return
    }
    const newStatuses = [...statuses, status]
    await setStatuses(newStatuses)
  }

  return { statuses, onSelectStatus, removeStatus, addStatus }
}
